/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import './PaginatedPopupCarousel.scss';

import * as React from 'react';

import { paginator } from 'shared-ui/utils/paginator';

import type { SearchResultItem } from '../../../api/search.types';
import { PaginatedPopupCarouselItem, NavigationCard } from './PaginatedPopupCarouselItem';

const ITEMS_PER_PAGE = 20;

interface PaginatedPopupCarouselProps {
    activeItemId: string | null;
    setActiveItemId: (id: string | null) => void;
    data?: Array<SearchResultItem>;
    fitBoundsToMarkers: () => void;
}

export function PaginatedPopupCarousel({
    data = [],
    setActiveItemId,
    activeItemId,
    fitBoundsToMarkers,
}: PaginatedPopupCarouselProps) {
    const [currentPage, setCurrentPage] = React.useState<number>(0);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const cardRefs = React.useRef<(HTMLDivElement | null)[]>([]);
    const nextActiveIdRef = React.useRef<string | null>(null);
    const observerRef = React.useRef<IntersectionObserver | null>(null);

    // Get paginated data
    const { paginatedData, previousPage, nextPage, totalPages } = React.useMemo(() => {
        return paginator({
            items: data,
            activeIndex: currentPage,
            perPage: ITEMS_PER_PAGE,
        });
    }, [data, currentPage]);

    // Calculate next and previous page item IDs
    // Changing pages via the navigation card is really just setting the active item ID to
    // the first item of the next or previous page
    // The actual page change is handled by useEffect that scrolls to the item
    const getNextPageItemId = React.useCallback(() => {
        if (nextPage !== null && nextPage < totalPages) {
            return data[nextPage * ITEMS_PER_PAGE]?.id || null;
        }
        return null;
    }, [nextPage, totalPages, data]);

    const getPreviousPageItemId = React.useCallback(() => {
        if (previousPage !== null) {
            return data[previousPage * ITEMS_PER_PAGE]?.id || null;
        }
        return null;
    }, [previousPage, data]);

    // Find the page that contains the active item
    const findPageForActiveItem = React.useCallback(
        (itemId: string) => {
            const itemIndex = data.findIndex((item) => item.id === itemId);
            if (itemIndex === -1) return 0;
            return Math.floor(itemIndex / ITEMS_PER_PAGE);
        },
        [data]
    );

    // Watch for card scroll changes so we can set the active item on the map to match the visible card
    const setUpObserver = React.useCallback(() => {
        if (!containerRef.current) return;

        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    // Check if item is mostly visible on screen
                    if (entry.intersectionRatio >= 0.8) {
                        const itemId = entry.target.getAttribute('data-item-id');

                        if (
                            itemId &&
                            activeItemId !== itemId &&
                            itemId !== nextActiveIdRef.current
                        ) {
                            nextActiveIdRef.current = itemId;
                            setActiveItemId(itemId);
                        }
                    }
                });
            },
            {
                root: containerRef.current,
                threshold: [0.8, 0.9, 1], // Multiple thresholds for precision
            }
        );

        // Observe all data items (not navigation items)
        cardRefs.current.forEach((card) => {
            if (card && card.getAttribute('data-item-id')) {
                observerRef.current?.observe(card);
            }
        });
    }, [activeItemId, setActiveItemId]);

    // On Mount fit the bounds to the map so all the items are visible when the carousel is visible, otherwise the pins might be under the carousel
    // Also using this effect's return to clean up the observer
    React.useEffect(() => {
        fitBoundsToMarkers();

        return () => {
            observerRef.current?.disconnect();
        };
    }, [fitBoundsToMarkers]);

    // Handle external active item changes and internal pagination changes.
    // Will jump to the correct page and scroll to item
    React.useEffect(() => {
        if (!activeItemId || activeItemId === nextActiveIdRef.current) return;

        observerRef.current?.disconnect();

        const requiredPage = findPageForActiveItem(activeItemId);

        // If we need to change pages, do it first
        if (requiredPage !== currentPage) {
            setCurrentPage(requiredPage);
            // Let the page change happen, then the next useEffect will handle scrolling
        } else {
            // Find the item in current display items and scroll to it
            const itemIndexInDisplay = paginatedData.findIndex(
                (item) => item.id === activeItemId
            );

            if (itemIndexInDisplay !== -1) {
                const container = containerRef.current;
                const targetElement = cardRefs.current[itemIndexInDisplay];

                if (container && targetElement) {
                    targetElement.scrollIntoView({
                        block: 'nearest',
                        inline: 'center',
                    });
                }
            }
            setUpObserver();
        }
    }, [activeItemId, currentPage, findPageForActiveItem, setUpObserver, paginatedData]);

    if (!data || data.length === 0) {
        return null;
    }

    return (
        <div className="paginated-popup-carousel">
            <div
                ref={containerRef}
                className="paginated-popup-carousel-items"
                style={{
                    gridTemplateColumns: `0 repeat(${paginatedData.length + 2}, minmax(85%, 1fr)) 0`,
                }}
            >
                <div className="spacer" role="presentation" />

                <NavigationCard
                    key={`navigation-card-start-${currentPage}`}
                    itemsPerPage={ITEMS_PER_PAGE}
                    dataLength={data.length}
                    pageIndex={currentPage}
                    onNextPage={
                        nextPage !== null && nextPage < totalPages
                            ? () => {
                                  const nextItemId = getNextPageItemId();
                                  setActiveItemId(nextItemId); // Set next active card
                              }
                            : undefined
                    }
                    onPreviousPage={
                        previousPage !== null
                            ? () => {
                                  const previousItemId = getPreviousPageItemId();
                                  setActiveItemId(previousItemId); // Set previous active card
                              }
                            : undefined
                    }
                    aria-posinset={1}
                    aria-setsize={paginatedData.length + 2}
                />

                {paginatedData.map((item, index) => {
                    return (
                        <PaginatedPopupCarouselItem
                            key={`data-${item.id}`}
                            ref={(el) => {
                                cardRefs.current[index] = el;
                            }}
                            cardData={item}
                            index={index}
                            aria-posinset={index + 2}
                            aria-setsize={paginatedData.length + 2}
                        />
                    );
                })}

                <NavigationCard
                    key={`navigation-card-end-${currentPage}`}
                    itemsPerPage={ITEMS_PER_PAGE}
                    dataLength={data.length}
                    pageIndex={currentPage}
                    onNextPage={
                        nextPage !== null && nextPage < totalPages
                            ? () => {
                                  const nextItemId = getNextPageItemId();
                                  setActiveItemId(nextItemId); // Set next active card
                              }
                            : undefined
                    }
                    onPreviousPage={
                        previousPage !== null
                            ? () => {
                                  const previousItemId = getPreviousPageItemId();
                                  setActiveItemId(previousItemId); // Set previous active card
                              }
                            : undefined
                    }
                    aria-posinset={paginatedData.length + 2}
                    aria-setsize={paginatedData.length + 2}
                />

                <div className="spacer" role="presentation" />
            </div>
        </div>
    );
}
